// DOM-centric Utility Functions
import animatedScrollTo from 'animated-scroll-to'
import { getCssVar } from './css'

export const createEvent = (
    type = null,
    eventInit = { bubbles: false, cancelable: true }
) => {
    if (!type)
        return null

    const isCustomEvent = typeof (eventInit || {}).detail === 'object'

    const isEventConstructor = (!isCustomEvent
            && typeof window.Event === 'function')
        || (isCustomEvent
            && typeof window.CustomEvent === 'function')

    const event = isEventConstructor
        ? (new window[`${isCustomEvent ? 'Custom' : ''}Event`](type, eventInit))
        : document.createEvent('HTMLEvents')

    if (!isEventConstructor)
        event.initEvent(type,
            (eventInit || {}).bubbles || false,
            (eventInit || {}).cancelable || true)

    if (isCustomEvent
            && !isEventConstructor)
        event.detail = eventInit.detail

    return event
}

export const debounce = (
    callback,
    delay = 200
) => {
    let timer

    return (...args) => {
        clearTimeout(timer)

        timer = setTimeout(() => callback.apply(null, args), delay)
    }
}

export const getBrowserClassList = () => {
    const classList = []

    const { userAgent } = window.navigator

    const isChrome = userAgent.match(/chrome/i)

    if (isChrome)
        classList.push('chrome')

    if (!isChrome
            && userAgent.match(/safari/i)) {
        classList.push('safari')

        if (userAgent.match(/linux/i))
            classList.push('linux')
    }

    if (userAgent.match(/firefox/i))
        classList.push('firefox')

    return classList
}

export const getScrollbarWidth = () => {
    const inner = document.createElement('p')
    const outer = document.createElement('div')

    inner.style.width = '100%'
    inner.style.height = '200px'

    outer.className = 'scrollbar-width-test'
    outer.style.position = 'absolute'
    outer.style.top = '-150px'
    outer.style.left = '-200px'
    outer.style.width = '200px'
    outer.style.height = '150px'
    outer.style.visibility = 'hidden'
    outer.style.overflow = 'scroll'

    outer.appendChild(inner)
    document.body.appendChild(outer)

    const width = outer.offsetWidth - inner.clientWidth
    document.body.removeChild(outer)

    return width
}

export const isTouchDevice = () => ('ontouchstart' in window
        && window.ontouchstart)
    || ('DocumentTouch' in window
        && document instanceof window.DocumentTouch)
    || ((window.navigator.maxTouchPoints || 0) > 0
        && !window.navigator.userAgent.match(/X11/))

export const scrollTo = async (numberOrArrayOrElement, opts = {}) => {
    const isArray = Array.isArray(numberOrArrayOrElement)
        && numberOrArrayOrElement
            .filter(number => typeof number === 'number')
            .length === 2

    const isNumber = !isArray
        && typeof numberOrArrayOrElement === 'number'

    const scrollToCoords = isArray
        ? numberOrArrayOrElement
        : [0, isNumber ? numberOrArrayOrElement : 0]

    const el = (numberOrArrayOrElement || {}).nodeType
        ? numberOrArrayOrElement
        : null

    if (!isArray
            && !isNumber
            && !el)
        return Promise.resolve(false)

    if (el) {
        let scrollY = el.getBoundingClientRect().top
            + (window.scrollY || 0)

        const header = document.getElementById('global-header')

        if (header
                && header.getAttribute('data-sticky-disabled') !== 'true') {
            const stickyNavOffset = parseFloat(
                getCssVar('--sticky-nav-offset') || '0')

            const stickyNavHeight = parseFloat(
                getCssVar('--sticky-nav-height') || '0')

            scrollY = scrollY > stickyNavOffset
                    && scrollY - stickyNavHeight > 0
                ? scrollY - stickyNavHeight
                : scrollY

            if (el.tagName === 'MAIN')
                scrollY = stickyNavOffset
        }

        scrollToCoords[1] = scrollY
    }

    const isSafari = window.navigator.userAgent.indexOf('Safari') >= 0
        && window.navigator.userAgent.indexOf('Chrome') === -1

    const focusEl = () => {
        if (!el)
            return

        if (el.tabIndex === -1
                && !el.getAttribute('tabindex'))
            el.setAttribute('tabindex', -1)

        el.focus({ preventScroll: true })
    }

    if (isSafari)
        focusEl()

    const isTouch = isTouchDevice()

    if (isTouch
            && !('cancelOnUserAction' in opts))
        opts.cancelOnUserAction = false

    const scrollToOpts = Object.assign({
        easing: t => t < 0.5
            ? 4 * t * t * t
            : (t - 1) * (2 * t - 2) * (2 * t - 2) + 1,
        speed: 200
    }, opts)

    return animatedScrollTo(scrollToCoords, scrollToOpts)
        .then((done) => {
            if (done
                    && !isSafari)
                focusEl()

            return done
        })
}

export const setDocumentTitle = (title) => {
    const docTitle = document.querySelector('title')

    const docTitleSuffix = docTitle.getAttribute('data-title-suffix')

    if (!title
            || (docTitleSuffix
                && title === docTitleSuffix))
        return docTitleSuffix
            && (docTitle.innerText = docTitleSuffix)

    docTitle.innerText = `${title}${
        docTitleSuffix
            ? ` | ${docTitleSuffix}`
            : ''}`
}

export const setSiblingsAriaHidden = (el, hidden) => (el || {}).parentElement
    && Array.from(el.parentElement.children)
        .filter(_el => _el !== el
            && (_el.getAttribute('aria-hidden') !== 'true'
                || _el.getAttribute('data-aria-hidden-restore') === 'true'))
        .forEach(_el => {
            if (hidden
                    && _el.getAttribute('aria-hidden') !== 'true')
                _el.setAttribute('aria-hidden', true)

            if (hidden
                    && _el.getAttribute('data-aria-hidden-restore') !== 'true')
                _el.setAttribute('data-aria-hidden-restore', true)

            if (!hidden
                    && _el.getAttribute('aria-hidden'))
                _el.removeAttribute('aria-hidden')

            if (!hidden
                    && _el.getAttribute('data-aria-hidden-restore'))
                _el.removeAttribute('data-aria-hidden-restore')
        })